import { Link } from 'react-router-dom';
import { Clock3, History } from 'lucide-react';
import type { SubjectActivitySummary } from '@shared/types/activity-spine';
import { Card } from '@/components/ui/Card';

interface ToolRecentActivityCardProps {
  summary: SubjectActivitySummary;
}

const TOOL_LABELS: Record<string, string> = {
  analyze_call: 'Analyser un appel',
  draft_followup: 'Préparer le suivi',
  prepare_meeting: 'Préparer le rendez-vous',
  pipeline_health_check: 'Diagnostic du pipeline',
  qualify_account: 'Qualifier un compte',
};

const EVENT_LABELS: Record<string, string> = {
  continuation_triggered: 'Suite lancée',
  tool_run_completed: 'Travail terminé',
  tool_run_failed: 'Travail interrompu',
  email_drafted: 'Brouillon créé',
  email_sent: 'Email envoyé',
  email_received: 'Réponse reçue',
  crm_deal_changed: 'Opportunité mise à jour',
  crm_activity_logged: 'Activité CRM',
  meeting_scheduled: 'Rendez-vous planifié',
  follow_up_pending: 'Suivi en attente',
  brief_not_prepared: 'Brief manquant',
  deal_stagnant: 'Opportunité sans avancée',
};

const formatEventTime = (value: string): string => new Date(value).toLocaleString('fr-FR', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const getItemHeadline = (eventType: string, outcomeSummary: string | null): string => (
  outcomeSummary?.trim() || EVENT_LABELS[eventType] || 'Événement récent'
);

export const ToolRecentActivityCard = ({ summary }: ToolRecentActivityCardProps) => {
  if (summary.items.length === 0) return null;

  return (
    <Card className="mx-5 border-domino-cardBorder bg-domino-warm p-5 shadow-[var(--domino-shadow-soft)] lg:mx-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-domino-ink">
            <History className="h-4 w-4 text-domino-aubergine" />
            <p className="text-sm font-semibold">Activité récente</p>
          </div>
          <p className="mt-1 text-sm font-medium text-domino-inkSoft">
            {summary.subjectLabel}
          </p>
        </div>
        {summary.latestEventAt && (
          <span className="text-xs text-domino-inkSoft">
            {formatEventTime(summary.latestEventAt)}
          </span>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {summary.items.map((item) => (
          <div key={item.id} className="rounded-2xl border border-domino-cardBorder bg-domino-muted px-4 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-6 text-domino-ink">
                  {getItemHeadline(item.eventType, item.outcomeSummary)}
                </p>
                <p className="mt-1 text-xs leading-5 text-domino-inkSoft">
                  {item.actorLabel}
                  {item.sourceToolKey ? ` · ${TOOL_LABELS[item.sourceToolKey] ?? item.sourceToolKey}` : ''}
                  {item.sourceConnector ? ` · ${item.sourceConnector}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1 text-xs text-domino-inkSoft">
                <Clock3 className="h-3.5 w-3.5" />
                <span>{formatEventTime(item.eventAt)}</span>
              </div>
            </div>
            {item.sourceRunId && item.sourceToolKey && (
              <div className="mt-3">
                <Link
                  to={`/tools/${item.sourceToolKey}?run=${item.sourceRunId}`}
                  className="inline-flex items-center text-xs font-medium text-domino-aubergine transition hover:text-domino-prune"
                >
                  Voir le résultat
                </Link>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
